import { useCallback, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';

interface FileUploaderProps {
  readonly onFileSelected: (file: File) => void;
  readonly isUploading: boolean;
  readonly progress: number;
  readonly accept?: string;
}

const ACCEPTED_TYPES = '.3mf';

export const FileUploader = ({
  onFileSelected,
  isUploading,
  progress,
  accept = ACCEPTED_TYPES,
}: FileUploaderProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (file: File) => {
      const ext = file.name.toLowerCase().split('.').pop();
      if (ext !== '3mf') return;
      onFileSelected(file);
    },
    [onFileSelected],
  );

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  }, []);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Upload 3D model file. Supports 3MF files up to 50MB."
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => inputRef.current?.click()}
      onKeyDown={handleKeyDown}
      style={{
        position: 'relative',
        cursor: isUploading ? 'default' : 'pointer',
        borderRadius: 'var(--radius-xl)',
        border: `2px dashed ${isDragging ? 'var(--color-primary-500)' : 'var(--border-default)'}`,
        backgroundColor: isDragging ? 'var(--color-primary-50)' : 'var(--bg-surface)',
        padding: 'var(--space-6) var(--space-4)',
        textAlign: 'center',
        opacity: isUploading ? 0.6 : 1,
        pointerEvents: isUploading ? 'none' : 'auto',
        transition: `all var(--duration-normal) var(--ease-out)`,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />

      <div
        className="mx-auto"
        style={{
          height: '3rem',
          width: '3rem',
          marginBottom: 'var(--space-4)',
          color: 'var(--text-tertiary)',
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
          />
        </svg>
      </div>

      <p
        style={{
          fontSize: 'var(--text-lg)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text-secondary)',
        }}
      >
        {isUploading ? 'Uploading...' : 'Drop your 3D model here'}
      </p>
      <p
        style={{
          marginTop: 'var(--space-1)',
          fontSize: 'var(--text-sm)',
          color: 'var(--text-tertiary)',
        }}
      >
        or click to browse. Supports 3MF files up to 50MB.
      </p>

      {isUploading && (
        <div style={{ marginTop: 'var(--space-6)' }}>
          <div
            className="mx-auto overflow-hidden"
            style={{
              height: '0.5rem',
              width: 'min(16rem, 100%)',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--color-neutral-200)',
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: 'var(--radius-full)',
                backgroundColor: 'var(--color-primary-500)',
                transition: `width var(--duration-slow) var(--ease-out)`,
                width: `${progress}%`,
              }}
            />
          </div>
          <p
            style={{
              marginTop: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-tertiary)',
            }}
          >
            {progress}%
          </p>
        </div>
      )}
    </div>
  );
};
